import { MICROSOFT365_IMPORT_MAX, type Microsoft365BrowseAt, type Microsoft365FileRowDto, type Microsoft365Source } from '@dbrain/shared';
import { Button, Dialog, IconButton, Icons } from '@dbrain/ui';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { isApiError } from '../../../api/client.js';
import { useDashboard, useMe } from '../../../api/hooks.js';
import { useMicrosoft365Status } from '../../../api/microsoft365.js';
import { useMicrosoft365Browse, useMicrosoft365Import } from '../../../api/microsoft365-files.js';
import { useMeta } from '../../../experience.js';
import { ErrorAlert } from '../../ErrorAlert.js';
import { ConsentPanel } from './ConsentPanel.js';
import { ImportResultCard, type ImportSummary } from './ImportResult.js';
import {
  addLabel,
  addMenuView,
  canAdd,
  copyNote,
  DEFAULT_SORT,
  emptyKey,
  errorAccess,
  footerParts,
  importTargetOf,
  nextSort,
  pickedOf,
  pickedTotals,
  pickerReturnTo,
  searchKey,
  siteOf,
  sortRows,
  sourceAccess,
  sourceWording,
  startOf,
  targetId,
  targetsFor,
  togglePicked,
  viewRows,
  type Access,
  type PickedFile,
  type RowSort,
} from './model.js';
import { PickerNav } from './PickerNav.js';
import { PickerTable } from './PickerTable.js';
import { TargetChooser } from './TargetChooser.js';

/** until the server says its upload limit */
const DEFAULT_MAX_MB = 100;
/** a typed search goes once typing pauses (each one is a Microsoft call) */
const SEARCH_PAUSE_MS = 500;

export interface FilePickerProps {
  /** where it opens */
  source: Microsoft365Source;
  /** opened from a composer: "Bu sohbet" is offered */
  composer: boolean;
  /** the conversation a composer's library pick is linked to */
  conversationId?: string;
  /** this page's path, for the way back from Microsoft's sign-in ("/", "/c/<id>", "/documents") */
  returnPath: string;
  /** a sign-in that came back with an error (`?microsoft365=error&reason=…`) */
  signInError?: string | null;
  onClose: () => void;
  /** "Bu sohbet": the page adds the files to its composer, one chip each, copied one by one like attachments */
  onAddToConversation?: (files: PickedFile[]) => void;
  /** a library import went through (the page says so, and links the documents on the home page) */
  onImported?: (summary: ImportSummary) => void;
}

/**
 * "Microsoft 365'ten belge ekle" (MsBelgeSec): a dialog (focus stays in, Escape closes, focus goes back) with the
 * sources on the left, the place in the folders as a breadcrumb, a search, the table, and on the right "Nereye
 * eklensin?". The selection (≤ 20) stays while the person moves between folders and sources; the footer counts and
 * sums it. Only files the person may see in Microsoft are listed (every call uses their own account). Library places
 * import at once and the worker copies the files ("İçe aktarılıyor" in Belgeler); "Bu sohbet" hands the files to the
 * composer. A failure per file shows MsDurumlar's card here; a missing permission shows "Yeni izin gerekiyor ·
 * yeniden bağlan", which comes back to this picker on the same source.
 */
export function FilePicker({ source: initial, composer, conversationId, returnPath, signInError, onClose, onAddToConversation, onImported }: FilePickerProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const limitId = useId();
  const status = useMicrosoft365Status().data;
  const me = useMe().data;
  const dashboard = useDashboard();
  const maxMb = useMeta().data?.uploadMaxMb ?? DEFAULT_MAX_MB;
  const menu = addMenuView(status, 'chat');
  const sources = menu?.sources ?? [initial];

  const [source, setSource] = useState<Microsoft365Source>(initial);
  const [at, setAt] = useState<Microsoft365BrowseAt>(() => startOf(initial));
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [followed, setFollowed] = useState<{ name: string; at: Microsoft365BrowseAt }[] | null>(null);
  const [picked, setPicked] = useState<ReadonlyMap<string, PickedFile>>(new Map());
  const [sort, setSort] = useState<RowSort>(DEFAULT_SORT);
  const [target, setTarget] = useState('user');
  const [acknowledged, setAcknowledged] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [refused, setRefused] = useState<Access | null>(null);
  const search = useRef<HTMLInputElement>(null);
  const [focusSearch, setFocusSearch] = useState(false);

  const base: Access | null = status ? sourceAccess(status, source) : null;
  const browse = useMicrosoft365Browse(at, query, base === 'ready' && refused === null);
  const browseRefusal = errorAccess(isApiError(browse.error) ? browse.error.code : undefined);
  const access: Access | null = base === 'ready' ? (refused ?? browseRefusal ?? 'ready') : base;
  const pages = useMemo(() => browse.data?.pages ?? [], [browse.data]);
  const first = pages[0];
  const rows = useMemo(() => sortRows(viewRows(pages), sort, i18n.language), [pages, sort, i18n.language]);

  // the SharePoint start names the followed sites ("SİTELER"); they stay while the person is inside a site
  useEffect(() => {
    if (first?.at.kind === 'sites' && !query && first.sites) setFollowed(first.sites);
  }, [first, query]);
  // a site with one library opens it at once (SharePoint › Finans › Belgeler)
  useEffect(() => {
    const only = rows[0];
    if (at.kind === 'site' && !query && !browse.hasNextPage && rows.length === 1 && only?.kind === 'library' && only.open) setAt(only.open);
  }, [at, query, rows, browse.hasNextPage]);
  // a typed search goes once typing pauses; clearing the box goes back to the folder
  useEffect(() => {
    const words = draft.trim();
    if (words === query) return;
    const timer = setTimeout(() => setQuery(words), SEARCH_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [draft, query]);
  useEffect(() => {
    if (!focusSearch) return;
    setFocusSearch(false);
    search.current?.focus();
  }, [focusSearch]);

  const targets = useMemo(
    () => targetsFor({ role: me?.tenant.role, workspaces: dashboard.data?.workspaces ?? [], conversation: composer }, i18n.language),
    [me?.tenant.role, dashboard.data?.workspaces, composer, i18n.language],
  );
  const chosen = targets.options.find((option) => targetId(option) === target) ?? targets.options[0] ?? { kind: 'user' as const };
  const pickedSources = [...picked.values()].map((file) => file.item.source);
  const wording = sourceWording(pickedSources, source);
  const note = copyNote(pickedSources, source, t);
  const totals = pickedTotals(picked);
  const footer = footerParts(totals.count, totals.bytes, i18n.language, t);
  const importer = useMicrosoft365Import();

  const go = (next: Microsoft365BrowseAt) => {
    setAt(next);
    setDraft('');
    setQuery('');
  };
  const pickSource = (next: Microsoft365Source) => {
    setSource(next);
    setRefused(null);
    go(startOf(next));
  };
  const toggle = (row: Microsoft365FileRowDto, on: boolean) => {
    const file = pickedOf(row, source, maxMb);
    if (!file) return;
    setPicked((current) => togglePicked(current, file, on) ?? current);
  };
  const choose = (id: string) => {
    setTarget(id);
    setAcknowledged(false);
  };
  const submit = () => {
    const files = [...picked.values()];
    if (!canAdd(files.length, chosen, acknowledged)) return;
    if (chosen.kind === 'conversation') {
      onAddToConversation?.(files);
      onClose();
      return;
    }
    const importTarget = importTargetOf(chosen, acknowledged);
    if (!importTarget) return;
    importer.mutate(
      { items: files.map((file) => file.item), target: importTarget, ...(conversationId ? { conversationId } : {}) },
      {
        onSuccess: (result) => {
          const done: ImportSummary = { result, target: chosen, names: files.map((file) => file.name) };
          if (result.failed.length > 0) {
            setSummary(done);
            return;
          }
          onImported?.(done);
          onClose();
        },
        onError: (error) => {
          const panel = errorAccess(isApiError(error) ? error.code : undefined);
          if (panel) setRefused(panel);
        },
      },
    );
  };
  const finish = () => {
    if (summary && summary.result.added.length > 0) onImported?.(summary);
    onClose();
  };

  const searchWords = searchKey(at, source);
  const crumbs = first?.crumbs ?? [];
  const full = picked.size >= MICROSOFT365_IMPORT_MAX;

  return (
    <Dialog
      bare
      title={t('microsoft365.files.picker.title')}
      labelledBy={titleId}
      closeLabel={t('common.close')}
      onClose={onClose}
      initialFocus="[data-picker-start]"
      className="m365-picker"
      data-testid="m365-picker"
    >
      <header className="m365-picker__head">
        <h2 id={titleId} className="m365-picker__title">
          {t('microsoft365.files.picker.title')}
        </h2>
        {status?.account ? <span className="m365-picker__account">{status.account.email}</span> : null}
        <IconButton label={t('common.close')} icon={<Icons.close />} onClick={onClose} data-testid="m365-picker-close" />
      </header>
      {summary ? (
        <div className="m365-picker__done">
          <ImportResultCard summary={summary} maxMb={maxMb} onDone={finish} />
        </div>
      ) : (
        <>
          <div className="m365-picker__body">
            <PickerNav
              sources={sources}
              source={source}
              sites={followed}
              currentSite={siteOf(first)}
              onSource={pickSource}
              onSite={(next) => {
                setSource('sharepoint');
                setRefused(null);
                go(next);
              }}
              onAllSites={() => {
                setSource('sharepoint');
                setRefused(null);
                go({ kind: 'sites' });
                setFocusSearch(true);
              }}
            />
            <div className="m365-picker__main">
              {access === null ? (
                <p className="m365-picker__state" role="status">
                  <span className="ui-spinner ui-spinner--inline" aria-hidden="true" />
                  {t('microsoft365.files.picker.loading')}
                </p>
              ) : access !== 'ready' ? (
                <ConsentPanel
                  access={access}
                  source={source}
                  returnTo={pickerReturnTo(returnPath, source)}
                  returned={signInError}
                  onRetry={() => {
                    setRefused(null);
                    void browse.refetch();
                  }}
                />
              ) : (
                <>
                  <div className="m365-picker__bar">
                    <nav className="m365-crumbs" aria-label={t('microsoft365.files.picker.crumbs')} data-testid="m365-picker-crumbs">
                      <ol>
                        {(crumbs.length > 0 ? crumbs : [{ name: t(`microsoft365.files.sources.${source}`), at: null }]).map((crumb, n, list) => (
                          <li key={`${crumb.name}-${n}`}>
                            {n === list.length - 1 ? (
                              <strong aria-current="location">{crumb.name}</strong>
                            ) : crumb.at ? (
                              <button type="button" className="m365-crumbs__link" onClick={() => crumb.at && go(crumb.at)}>
                                {crumb.name}
                              </button>
                            ) : (
                              <span>{crumb.name}</span>
                            )}
                          </li>
                        ))}
                      </ol>
                    </nav>
                    {searchWords ? (
                      <label className="search-pill m365-picker__search">
                        <Icons.search size={16} />
                        <span className="ui-visually-hidden">{t(`microsoft365.files.picker.search.${searchWords}`)}</span>
                        <input
                          ref={search}
                          type="search"
                          value={draft}
                          placeholder={t(`microsoft365.files.picker.search.${searchWords}`)}
                          maxLength={200}
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              setQuery(draft.trim());
                            }
                          }}
                          data-testid="m365-picker-search"
                        />
                      </label>
                    ) : null}
                  </div>
                  {query ? (
                    <p className="m365-picker__results" data-testid="m365-picker-results">
                      {t('microsoft365.files.picker.results', { query })}
                      <button type="button" className="link-button" onClick={() => go(at)}>
                        {t('microsoft365.files.picker.clearSearch')}
                      </button>
                    </p>
                  ) : null}
                  {browse.isPending ? (
                    <p className="m365-picker__state" role="status" data-testid="m365-picker-loading">
                      <span className="ui-spinner ui-spinner--inline" aria-hidden="true" />
                      {t('microsoft365.files.picker.loading')}
                    </p>
                  ) : browse.isError ? (
                    <ErrorAlert
                      error={browse.error}
                      action={
                        <Button size="sm" variant="secondary" onClick={() => void browse.refetch()} data-testid="m365-picker-retry">
                          {t('microsoft365.files.picker.retry')}
                        </Button>
                      }
                    />
                  ) : rows.length === 0 ? (
                    <p className="m365-picker__state" data-testid="m365-picker-empty">
                      {t(`microsoft365.files.picker.empty.${emptyKey(at, query)}`)}
                    </p>
                  ) : (
                    <PickerTable rows={rows} source={source} picked={picked} sort={sort} onSort={(key) => setSort(nextSort(sort, key))} onOpen={go} onToggle={toggle} maxMb={maxMb} limitId={limitId} />
                  )}
                  {browse.hasNextPage ? (
                    <div className="m365-picker__more">
                      <Button size="sm" variant="secondary" busy={browse.isFetchingNextPage} onClick={() => void browse.fetchNextPage()} data-testid="m365-picker-more">
                        {t('microsoft365.files.picker.more')}
                      </Button>
                    </div>
                  ) : null}
                </>
              )}
              {full ? (
                <p className="m365-picker__limit" id={limitId} role="status" data-testid="m365-picker-limit">
                  <Icons.info size={15} />
                  {t('microsoft365.files.picker.limit', { max: MICROSOFT365_IMPORT_MAX })}
                </p>
              ) : null}
              <p className="m365-picker__note" data-testid="m365-picker-note">
                {t('microsoft365.files.picker.note', { max: MICROSOFT365_IMPORT_MAX })}
              </p>
            </div>
            <aside className="m365-picker__aside" aria-label={t('microsoft365.files.targets.legend')}>
              <TargetChooser
                targets={targets}
                value={targetId(chosen)}
                onChange={choose}
                acknowledged={acknowledged}
                onAcknowledge={setAcknowledged}
                wording={wording}
                testId="m365-picker-targets"
              />
              <p className="m365-picker__copy">
                <Trans i18nKey={note.key} values={{ source: note.source }} components={{ b: <strong /> }} />
              </p>
            </aside>
          </div>
          <footer className="m365-picker__foot">
            {importer.error && !errorAccess(isApiError(importer.error) ? importer.error.code : undefined) ? (
              <div className="m365-picker__error">
                <ErrorAlert error={importer.error} />
              </div>
            ) : null}
            <span className="m365-picker__count" aria-live="polite" data-testid="m365-picker-count">
              <strong>{footer.lead}</strong>
              {footer.size ? ` · ${footer.size}` : null}
            </span>
            <Button variant="secondary" onClick={onClose} data-testid="m365-picker-cancel">
              {t('common.cancel')}
            </Button>
            <Button disabled={!canAdd(totals.count, chosen, acknowledged)} busy={importer.isPending} onClick={submit} data-testid="m365-picker-add">
              {addLabel(totals.count, t)}
            </Button>
          </footer>
        </>
      )}
    </Dialog>
  );
}
